/**
 * PermissionMatrixEditor — the single, shared permission-editing UI.
 *
 * Renders the exact same layout used everywhere permissions are edited so
 * there is zero visual/behavioural drift between the Admin → Users dialog
 * and the Permission Templates editor:
 *   • Material Access Matrix (4 materials × 4 levels)
 *   • Reports Access matrix (report × View / See Value / Print)
 *   • Financial Visibility (rate/value contexts)
 *   • System (Activity Logs)
 *   • Other Permissions (remaining catalog groups as matrix-style tables)
 *
 * Fully controlled: pass the current ``permissions`` array + an ``onChange``
 * callback that receives the FULL next array on every edit. ``idPrefix``
 * namespaces the data-testids ('user' for the Users dialog, 'template' for
 * the Templates editor).
 */
import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

// Ordered materials for the 4×4 access matrix. Keys match the backend
// permission catalog (``materials.<mat>.<level>``). Mountings uses
// ``edit_master`` as its 2nd column key (no per-order requirements), so the
// helpers below translate ``edit_requirement`` → ``edit_master`` for it.
export const MATRIX_MATERIALS = [
  { key: 'gold', label: 'Gold', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  { key: 'diamonds', label: 'Diamonds', tone: 'bg-slate-50 text-slate-800 border-slate-200' },
  { key: 'color_stones', label: 'Colour Stones', tone: 'bg-rose-50 text-rose-800 border-rose-200' },
  { key: 'pearls', label: 'Pearls', tone: 'bg-stone-50 text-stone-800 border-stone-200' },
  { key: 'mountings', label: 'Mountings', tone: 'bg-orange-50 text-orange-800 border-orange-200' },
];
export const MATRIX_LEVELS = [
  { key: 'see', label: 'See', hint: 'View requirements + ledgers + reports for this material' },
  { key: 'edit_requirement', label: 'Edit Requirement', hint: 'Add / edit / delete requirements (Mountings: manage master list of chains / pech / chaki / findings)' },
  { key: 'record_purchase', label: 'Record Purchase', hint: 'Receive stock into packets, rates, ledger entries' },
  { key: 'issue', label: 'Issue to Goldsmith', hint: 'Issue this material to a goldsmith within an order' },
];
const MATERIAL_LEVEL_KEY_OVERRIDE = {
  mountings: { edit_requirement: 'edit_master' },
};
export const permKeyFor = (mat, level) => {
  const overrides = MATERIAL_LEVEL_KEY_OVERRIDE[mat];
  const resolved = overrides && overrides[level] ? overrides[level] : level;
  return `materials.${mat}.${resolved}`;
};

export const REPORTS_MATRIX = [
  { key: 'unified_closing', label: 'Unified Closing Stock', hint: 'Stock in hand + on-approval + WIP across every material' },
  { key: 'memos', label: 'Issue & Return Memos', hint: 'Browse every issue and return memo across parties' },
  { key: 'diamond_closing', label: 'Diamond Closing Balance', hint: 'Classification-wise diamond stock', materialGuard: 'diamonds' },
  { key: 'color_stone_closing', label: 'Colour Stone Closing Balance', hint: 'Classification-wise colour stone stock', materialGuard: 'color_stones' },
  { key: 'pearl_closing', label: 'Pearl Closing Balance', hint: 'Classification-wise pearl stock', materialGuard: 'pearls' },
  { key: 'gold_ledger', label: 'Gold Ledger', hint: 'Full 999 gold ledger with running balance', materialGuard: 'gold' },
  { key: 'mountings_closing', label: 'Mountings Closing Balance', hint: 'Per-item semi-finished gold pieces closing stock (grams + live gold-rate valuation)', materialGuard: 'mountings' },
  { key: 'jewellery', label: 'Jewellery Stock', hint: 'Finished-piece register with purity, weights, and totals' },
  // Wave 4.07 — CAD Development report is a pure count (no monetary value),
  // so it only supports View + Print (no "See Value").
  { key: 'cad', label: 'CAD Development', hint: 'Count of CADs developed — filter by CAD Developer, Page No, Order No, Customer', levels: ['view', 'print'] },
  // Production Stage Digest — pivot of active order forms by production stage.
  // On-screen only for now, so it supports View (no "See Value" / "Print").
  { key: 'production_digest', label: 'Production Stage Digest', hint: 'Active order forms per stage — Customer / Goldsmith-wise pivot with drill-down', levels: ['view'] },
  // Estimated Delivery — active order forms grouped by expected delivery date.
  // Supports View + Print / Export (no monetary "See Value"). Sales roles see
  // only their own attributed customers.
  { key: 'estimated_delivery', label: 'Estimated Delivery Report', hint: 'Active order forms grouped by expected delivery date — Daily / Weekly / Monthly, Customer / Goldsmith-wise', levels: ['view', 'print'] },
  // Customer Gold Report — company-wide advance gold & net balances across
  // customers (advance-only filtering + per-customer drill-down). View-only
  // tile; independent of the per-customer gold ledger dialog permission.
  { key: 'customer_gold', label: 'Customer Gold Report', hint: 'Advance gold & net balances across all customers — advance-only filtering and per-customer drill-down', levels: ['view'] },
];
// Levels a given report row supports (defaults to every level).
const reportRowLevels = (rep) => rep.levels || REPORTS_LEVELS.map((l) => l.key);
export const REPORTS_LEVELS = [
  { key: 'view', label: 'View', hint: 'Sees the report tile and can open the report page' },
  { key: 'value', label: 'See Value', hint: 'Sees rate + value + total figures inside this report' },
  { key: 'print', label: 'Print / Export', hint: 'Can download PDF and Excel exports of the report' },
];

const VALUES_CONTEXTS = [
  {
    key: 'jewellery_embedded',
    label: 'Jewellery — Embedded Material',
    hint: 'Cost of raw material embedded inside a finished piece (materials-at-cost breakdown on jewellery detail). Auto-enabled when Reports → Jewellery Stock → See Value is on.',
  },
];

// Catalog groups already represented by the matrices above — hidden from the
// "Other Permissions" section so admins never double-toggle the same key.
const MATRIX_GROUP_NAMES = new Set([
  'Material Access Matrix',
  'Reports & Logs',
  'Financial Visibility',
  'Diamonds', // legacy — aliased to materials.diamonds.*
  'Gold',     // legacy — aliased to materials.gold.*
]);
const HIDDEN_KEYS = new Set(['color_stones.view', 'pearls.view']);

export function PermissionMatrixEditor({
  permissions = [],
  onChange,
  permGroups = [],
  idPrefix = 'user',
  allKeys: allKeysProp,
  children,
}) {
  const has = (k) => permissions.includes(k);
  const allKeys = useMemo(
    () => (allKeysProp && allKeysProp.length
      ? allKeysProp
      : permGroups.flatMap((g) => (g.permissions || []).map((p) => p.key))),
    [allKeysProp, permGroups],
  );

  const nonMaterialGroups = useMemo(
    () => permGroups
      .filter((g) => !MATRIX_GROUP_NAMES.has(g.group))
      .map((g) => ({ ...g, permissions: g.permissions.filter((p) => !HIDDEN_KEYS.has(p.key)) }))
      .filter((g) => g.permissions.length > 0),
    [permGroups],
  );

  // ---- Mutators (always emit the FULL next array) --------------------
  const togglePerm = (k) => onChange(has(k) ? permissions.filter((x) => x !== k) : [...permissions, k]);
  const selectAll = () => onChange([...allKeys]);
  const clearAll = () => onChange([]);

  const toggleMatrixCell = (mat, lv) => togglePerm(permKeyFor(mat, lv));
  const setMatrixRow = (mat, checked) => {
    const keys = MATRIX_LEVELS.map((lv) => permKeyFor(mat, lv.key));
    const without = permissions.filter((k) => !keys.includes(k));
    onChange(checked ? [...without, ...keys] : without);
  };
  const setMatrixColumn = (lv, checked) => {
    const keys = MATRIX_MATERIALS.map((m) => permKeyFor(m.key, lv));
    const without = permissions.filter((k) => !keys.includes(k));
    onChange(checked ? [...without, ...keys] : without);
  };

  const toggleReportCell = (rep, lv) => {
    const cellKey = `reports.${rep}.${lv}`;
    const viewKey = `reports.${rep}.view`;
    const valueKey = `reports.${rep}.value`;
    const printKey = `reports.${rep}.print`;
    const had = permissions.includes(cellKey);
    let next = had ? permissions.filter((x) => x !== cellKey) : [...permissions, cellKey];
    if (!had) {
      if ((lv === 'value' || lv === 'print') && !next.includes(viewKey)) next = [...next, viewKey];
    } else if (lv === 'view') {
      next = next.filter((x) => x !== valueKey && x !== printKey);
    }
    onChange(next);
  };
  const setReportRow = (rep, checked) => {
    const repObj = REPORTS_MATRIX.find((r) => r.key === rep) || { key: rep };
    const keys = reportRowLevels(repObj).map((lvKey) => `reports.${rep}.${lvKey}`);
    const without = permissions.filter((k) => !keys.includes(k));
    onChange(checked ? [...without, ...keys] : without);
  };
  const setReportColumn = (lv, checked) => {
    const rows = REPORTS_MATRIX.filter((r) => reportRowLevels(r).includes(lv));
    const keys = rows.map((r) => `reports.${r.key}.${lv}`);
    const without = permissions.filter((k) => !keys.includes(k));
    let next = checked ? [...without, ...keys] : without;
    if (checked && (lv === 'value' || lv === 'print')) {
      const viewKeys = rows.map((r) => `reports.${r.key}.view`);
      next = [...next.filter((k) => !viewKeys.includes(k)), ...viewKeys];
    } else if (!checked && lv === 'view') {
      const dependentKeys = rows.flatMap((r) => [`reports.${r.key}.value`, `reports.${r.key}.print`]);
      next = next.filter((k) => !dependentKeys.includes(k));
    }
    onChange(next);
  };

  const toggleValueCell = (ctx) => togglePerm(`values.${ctx}.view`);

  return (
    <div data-testid={`${idPrefix}-permission-matrix`}>
      <div className="flex items-center justify-between mb-1">
        <Label>Permissions ({permissions.length})</Label>
        <div className="flex gap-2">
          <button type="button" onClick={selectAll} className="text-[11px] text-[var(--ink-navy)] hover:underline" data-testid={`${idPrefix}-perm-select-all`}>Select all</button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={clearAll} className="text-[11px] text-slate-500 hover:underline" data-testid={`${idPrefix}-perm-clear-all`}>Clear</button>
        </div>
      </div>

      {children}

      {/* Material Access Matrix */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Material Access Matrix</div>
        <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
          <table className="w-full text-xs" data-testid={`${idPrefix}-material-matrix`}>
            <thead className="bg-[var(--paper-warm)]">
              <tr>
                <th className="text-left px-2 py-2 font-medium text-slate-500 uppercase tracking-wide">Material</th>
                {MATRIX_LEVELS.map((lv) => {
                  const colKeys = MATRIX_MATERIALS.map((m) => permKeyFor(m.key, lv.key));
                  const allChecked = colKeys.every((k) => has(k));
                  return (
                    <th key={lv.key} className="text-center px-2 py-2 font-medium text-slate-600" title={lv.hint}>
                      <div className="flex flex-col items-center gap-1">
                        <span>{lv.label}</span>
                        <Checkbox checked={allChecked} onCheckedChange={(v) => setMatrixColumn(lv.key, !!v)} data-testid={`${idPrefix}-matrix-col-${lv.key}`} className="h-3 w-3" />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {MATRIX_MATERIALS.map((mat) => {
                const rowKeys = MATRIX_LEVELS.map((lv) => permKeyFor(mat.key, lv.key));
                const allRowChecked = rowKeys.every((k) => has(k));
                return (
                  <tr key={mat.key} data-testid={`${idPrefix}-matrix-row-${mat.key}`}>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={allRowChecked} onCheckedChange={(v) => setMatrixRow(mat.key, !!v)} data-testid={`${idPrefix}-matrix-row-toggle-${mat.key}`} className="h-3 w-3" />
                        <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-medium ${mat.tone}`}>{mat.label}</span>
                      </div>
                    </td>
                    {MATRIX_LEVELS.map((lv) => {
                      const key = permKeyFor(mat.key, lv.key);
                      const cellTitle = (mat.key === 'mountings' && lv.key === 'edit_requirement')
                        ? 'Manage the master list of mountings / chains / pech / chaki / findings (backend key: materials.mountings.edit_master)'
                        : lv.hint;
                      return (
                        <td key={lv.key} className="px-2 py-2 text-center" title={cellTitle}>
                          <Checkbox checked={has(key)} onCheckedChange={() => toggleMatrixCell(mat.key, lv.key)} data-testid={`${idPrefix}-matrix-cell-${mat.key}-${lv.key}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          Materials outside the operator&apos;s <span className="font-medium">See</span> scope are completely hidden across Orders, Ledgers, Reports and Dashboard.
        </div>
      </div>

      {/* Reports Access matrix */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Reports Access</div>
        <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
          <table className="w-full text-xs" data-testid={`${idPrefix}-reports-matrix`}>
            <thead className="bg-[var(--paper-warm)]">
              <tr>
                <th className="text-left px-2 py-2 font-medium text-slate-500 uppercase tracking-wide">Report</th>
                {REPORTS_LEVELS.map((lv) => {
                  const colRows = REPORTS_MATRIX.filter((r) => reportRowLevels(r).includes(lv.key));
                  const colKeys = colRows.map((r) => `reports.${r.key}.${lv.key}`);
                  const allChecked = colKeys.length > 0 && colKeys.every((k) => has(k));
                  return (
                    <th key={lv.key} className="text-center px-2 py-2 font-medium text-slate-600" title={lv.hint}>
                      <div className="flex flex-col items-center gap-1">
                        <span>{lv.label}</span>
                        <Checkbox checked={allChecked} onCheckedChange={(v) => setReportColumn(lv.key, !!v)} data-testid={`${idPrefix}-reports-col-${lv.key}`} className="h-3 w-3" />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {REPORTS_MATRIX.map((rep) => {
                const supported = reportRowLevels(rep);
                const rowKeys = REPORTS_LEVELS.filter((lv) => supported.includes(lv.key)).map((lv) => `reports.${rep.key}.${lv.key}`);
                const allRowChecked = rowKeys.length > 0 && rowKeys.every((k) => has(k));
                const guardWarn = rep.materialGuard && !has(`materials.${rep.materialGuard}.see`);
                return (
                  <tr key={rep.key} data-testid={`${idPrefix}-reports-row-${rep.key}`}>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={allRowChecked} onCheckedChange={(v) => setReportRow(rep.key, !!v)} data-testid={`${idPrefix}-reports-row-toggle-${rep.key}`} className="h-3 w-3" />
                        <div>
                          <div className="font-medium text-slate-700">{rep.label}</div>
                          <div className="text-[10px] text-slate-500">{rep.hint}</div>
                          {guardWarn && (
                            <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`${idPrefix}-reports-guard-${rep.key}`}>
                              Needs <span className="font-mono">materials.{rep.materialGuard}.see</span> to actually surface.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {REPORTS_LEVELS.map((lv) => {
                      const key = `reports.${rep.key}.${lv.key}`;
                      if (!supported.includes(lv.key)) {
                        return (
                          <td key={lv.key} className="px-2 py-2 text-center text-slate-300" data-testid={`${idPrefix}-reports-cell-${rep.key}-${lv.key}-na`}>
                            —
                          </td>
                        );
                      }
                      return (
                        <td key={lv.key} className="px-2 py-2 text-center">
                          <Checkbox checked={has(key)} onCheckedChange={() => toggleReportCell(rep.key, lv.key)} data-testid={`${idPrefix}-reports-cell-${rep.key}-${lv.key}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          <span className="font-medium">View</span> shows the tile · <span className="font-medium">See Value</span> reveals rate + total figures inside the report · <span className="font-medium">Print</span> shows the PDF / Excel buttons. Enabling <span className="font-medium">See Value</span> or <span className="font-medium">Print</span> automatically enables <span className="font-medium">View</span> — you can&apos;t export or reveal values on a report you can&apos;t open. A material-specific report is silently hidden if the user also lacks that material&apos;s See scope above.
        </div>
      </div>

      {/* Financial Visibility */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Financial Visibility (Rate / Value)</div>
        <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
          <table className="w-full text-xs" data-testid={`${idPrefix}-values-matrix`}>
            <thead className="bg-[var(--paper-warm)]">
              <tr>
                <th className="text-left px-2 py-2 font-medium text-slate-500 uppercase tracking-wide">Context</th>
                <th className="text-center px-2 py-2 font-medium text-slate-600 w-32">See Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {VALUES_CONTEXTS.map((ctx) => {
                const key = `values.${ctx.key}.view`;
                const rawChecked = has(key);
                const coupledToReport = ctx.key === 'jewellery_embedded' && has('reports.jewellery.value');
                const checked = coupledToReport ? true : rawChecked;
                return (
                  <tr key={ctx.key} data-testid={`${idPrefix}-values-row-${ctx.key}`}>
                    <td className="px-2 py-2">
                      <div className="font-medium text-slate-700">{ctx.label}</div>
                      <div className="text-[10px] text-slate-500">{ctx.hint}</div>
                      {coupledToReport && (
                        <div className="mt-1 text-[10px] text-emerald-700 font-medium" data-testid={`${idPrefix}-values-locked-${ctx.key}`}>
                          Auto-enabled by Reports → Jewellery Stock → See Value.
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Checkbox
                        checked={checked}
                        disabled={coupledToReport}
                        onCheckedChange={() => { if (coupledToReport) return; toggleValueCell(ctx.key); }}
                        data-testid={`${idPrefix}-values-cell-${ctx.key}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          Rate / value across raw-material screens follows each material report&apos;s <span className="font-medium">See Value</span> toggle above. Legacy <span className="font-mono">values.view</span> automatically grants all value contexts, so existing users retain full visibility on redeploy.
        </div>
      </div>

      {/* System */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">System</div>
        <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
          <table className="w-full text-xs" data-testid={`${idPrefix}-system-matrix`}>
            <thead className="bg-[var(--paper-warm)]">
              <tr>
                <th className="text-left px-2 py-2 font-medium text-slate-500 uppercase tracking-wide">Capability</th>
                <th className="text-center px-2 py-2 font-medium text-slate-600 w-24">Grant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              <tr data-testid={`${idPrefix}-system-row-logs-view`}>
                <td className="px-2 py-2">
                  <div className="font-medium text-slate-700">Activity Logs</div>
                  <div className="text-[10px] text-slate-500">See the portal-wide <span className="font-mono">/logs</span> page (system events, comments, milestones, issues / returns, edits) plus the per-order Activity Log section on Order Detail. Off by default — grant only to auditors / admins.</div>
                </td>
                <td className="px-2 py-2 text-center">
                  <Checkbox checked={has('logs.view')} onCheckedChange={() => togglePerm('logs.view')} data-testid={`${idPrefix}-system-cell-logs-view`} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Other Permissions */}
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Other Permissions</div>
      <div className="space-y-3">
        {nonMaterialGroups.map((grp) => (
          <div key={grp.group} className="overflow-x-auto border border-[var(--divider)] rounded-md">
            <table className="w-full text-xs" data-testid={`${idPrefix}-perm-group-${grp.group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <thead className="bg-[var(--paper-warm)]">
                <tr>
                  <th className="text-left px-2 py-2 font-medium text-slate-500 uppercase tracking-wide">{grp.group}</th>
                  <th className="text-center px-2 py-2 font-medium text-slate-600 w-24">Grant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--divider)]">
                {grp.permissions.map((p) => (
                  <tr key={p.key} data-testid={`${idPrefix}-perm-row-${p.key}`}>
                    <td className="px-2 py-2">
                      <div className="text-slate-700">{p.label}</div>
                      <div className="font-mono text-[10px] text-slate-400">{p.key}</div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={has(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`${idPrefix}-perm-${p.key}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PermissionMatrixEditor;
